<script setup lang="ts">
  import CalendarSettingsPanel from '@/components/app/calendar/CalendarSettingsPanel.vue'
  import { useCalendarKindChecks } from '@/composables/useCalendarKindChecks'
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import { useConnectedCalendars } from '@/composables/useConnectedCalendars'
  import { useTranslation } from '@/composables/useTranslation'
  import { genericErrorToast } from '@/lib/toasts'
  import { useUserStore } from '@/shared/stores/userStore'
  import { AccountsService, type TUser } from '@/types/generated'
  import { ApiError } from '@/types/generated/core/ApiError'
  import {
    calendarAuthErrorTranslations,
    getCalendarAuthErrorMessage
  } from '@/utils/calendar/auth-errors'
  import { computed, onMounted, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'

  const { t } = useTranslation({
    en: {
      loading: 'Loading calendar settings...',
      loadFailed: 'Could not load calendar settings.',
      retry: 'Try again',
      ...calendarAuthErrorTranslations.en
    },
    de: {
      loading: 'Kalendereinstellungen werden geladen...',
      loadFailed: 'Kalendereinstellungen konnten nicht geladen werden.',
      retry: 'Erneut versuchen',
      ...calendarAuthErrorTranslations.de
    }
  })

  const userStore = useUserStore()
  const route = useRoute()
  const router = useRouter()
  const currentUser = computed<TUser | null>(() => userStore.user ?? null)
  const { calendars, isLoading, loadError, loadCalendars } = useConnectedCalendars()
  const disconnectingAccountUuid = ref<string | null>(null)
  const { calendarKindChecks, setAccountKindSync } = useCalendarKindChecks(calendars)

  const extractCalendarErrorMessage = (error: unknown): string | undefined => {
    if (!(error instanceof ApiError) || !error.body || typeof error.body !== 'object') {
      return undefined
    }

    const body = error.body as Record<string, unknown>
    if (typeof body.detail === 'string') {
      return body.detail
    }
    if (typeof body.error === 'string') {
      return body.error
    }

    for (const value of Object.values(body)) {
      if (typeof value === 'string') {
        return value
      }
      if (Array.isArray(value) && typeof value[0] === 'string') {
        return value[0]
      }
    }

    return undefined
  }

  const showLoading = computed(
    () =>
      !currentUser.value || (isLoading.value && calendars.value.length === 0 && !loadError.value)
  )

  const consumeCalendarSettingsQuery = async (): Promise<void> => {
    const authError = getCalendarAuthErrorMessage(route.query.calendarAuthError, t)
    const shouldCleanQuery =
      route.query.calendarSettings === '1' || typeof route.query.calendarAuthError === 'string'

    if (authError) {
      genericErrorToast(authError)
    }

    if (!shouldCleanQuery) {
      return
    }

    const nextQuery = { ...route.query }
    delete nextQuery.calendarSettings
    delete nextQuery.calendarAuthError
    await router.replace({ query: nextQuery })
  }

  onMounted(() => {
    void consumeCalendarSettingsQuery()
  })

  const disconnectAccount = async (accountUuid: string): Promise<void> => {
    disconnectingAccountUuid.value = accountUuid

    try {
      await AccountsService.accountsDestroy({ uuid: accountUuid })
      await loadCalendars()
    } catch (error) {
      console.error('[calendar-settings] disconnectAccount failed', error)
      genericErrorToast(extractCalendarErrorMessage(error))
    } finally {
      disconnectingAccountUuid.value = null
    }
  }
</script>

<template>
  <SettingsPage>
    <div
      v-if="showLoading"
      class="text-muted-foreground-1 text-sm"
    >
      {{ t('loading') }}
    </div>

    <div
      v-else-if="loadError"
      class="space-y-3"
    >
      <p class="text-sm text-foreground">{{ t('loadFailed') }}</p>
      <button
        type="button"
        class="focus:outline-hidden inline-flex items-center rounded-md bg-primary px-3 py-1.5 text-xs
          text-primary-foreground hover:bg-primary-hover"
        @click="void loadCalendars()"
      >
        {{ t('retry') }}
      </button>
    </div>

    <CalendarSettingsPanel
      v-else
      :calendars="calendars"
      :calendar-kind-checks="calendarKindChecks"
      :current-user="currentUser"
      :disconnecting-account-uuid="disconnectingAccountUuid"
      :accounts-loading="isLoading"
      class="-mx-5 -my-4"
      @disconnect-account="disconnectAccount"
      @set-account-kind-sync="setAccountKindSync"
    />
  </SettingsPage>
</template>
